/**
 * @file RgsSpinAdapter.ts
 * @role Pure wire->DTO adapter for the deployed RGS: takes an already-validated wire payload and
 *       reshapes it into the SpinResultDto gameplay consumes. No I/O, no `this`, no state — so
 *       every rounding and every "the server does not send this yet" fallback is testable in
 *       isolation. When the server ships minor units, round ids and balanceBefore (#53), the
 *       deletions are all in this one file.
 * @owns Minor-unit conversion, payline position reconstruction, feature adaptation.
 * @reads Validated RGS wire types and the session context the client holds.
 * @neverDoes Call the network. Decide outcomes or win amounts (the RGS did). Hold state.
 * @invariants 1 (reconstructs what the server decided, never re-decides it), 2 (win amounts
 *             come from the server), 7 (integer minor units past the conversion boundary).
 * @see docs/BACKEND_CONTRACT.md, src/core/backend/HttpRgsClient.ts
 */
import { GameError } from '@core/errors/GameError';
import type {
  FeatureSpinDto,
  FeatureTriggerDto,
  GridPosition,
  SpinRequestDto,
  SpinResultDto,
  WinDto,
} from './dto';
import type { RgsFeatureWin, RgsPaylineWin, RgsSpinResponse } from './validators';

/** What the client knows about the live session that the adapter needs to read. */
export interface RgsSessionContext {
  sessionId: string;
  currency: string;
  currencyExponent: number;
  /** The stake the SERVER will settle the next round at. The RGS reads the bet from the session,
   *  not from the spin body, so this — not the HUD — is what the next round actually costs. */
  betAmountMinor: number;
  /** `rowPerReel` per payline id — the key to reconstructing win positions the RGS omits. */
  paylines: Map<string, readonly number[]>;
}

export interface RgsAdaptOptions {
  /** Symbol whose occurrences mark a feature trigger on the base screen. */
  scatterSymbolId?: string;
}

/**
 * Decimal major units -> integer minor units (Invariant 7).
 *
 * The server sends `decimal`; JavaScript parses it as a float, and `964.9 * 100` is
 * `96489.99999999999`. The rounding error is ~1e-10 relative, many orders of magnitude below the
 * 0.5 that Math.round arbitrates, so this is exact for every amount a wallet can hold. Once the
 * server implements A4 the conversion is skipped entirely in favour of its own integers.
 */
export function toMinorUnits(value: number, exponent: number): number {
  return Math.round(value * 10 ** exponent);
}

/** Integer minor units -> the decimal major units the RGS wire format still expects (pre-A4). */
export function toMajorUnits(minor: number, exponent: number): number {
  return minor / 10 ** exponent;
}

/**
 * Reconstruct the grid cells a payline win covers. The RGS reports `paylineId` + `symbolCount`
 * but no positions; the session's payline definitions carry `rowPerReel`, and ReelCore's
 * LeftToRightEvaluator always matches from reel 0 rightwards. So the covered cells are the
 * first `symbolCount` reels of that line — a deterministic reconstruction of what the server
 * already decided, not a re-evaluation of the outcome (Invariant 1).
 */
export function paylinePositions(win: RgsPaylineWin, context: RgsSessionContext): GridPosition[] {
  const rowPerReel = context.paylines.get(win.paylineId);
  if (!rowPerReel) {
    throw new GameError(
      'SPIN_RESPONSE_INVALID',
      `RGS reported a win on unknown payline '${win.paylineId}'.`,
    );
  }
  return rowPerReel.slice(0, win.symbolCount).map((row, reel) => ({ reel, row }));
}

function adaptPaylineWin(win: RgsPaylineWin, winId: string, context: RgsSessionContext): WinDto {
  return {
    winId,
    type: 'line',
    amountMinor: toMinorUnits(win.win, context.currencyExponent),
    positions: paylinePositions(win, context),
    symbolId: win.symbolId,
  };
}

/** Cells on the base screen holding the scatter — what visually triggered the feature. */
function triggerPositions(
  grid: readonly (readonly string[])[],
  scatterSymbolId: string | undefined,
): GridPosition[] {
  if (!scatterSymbolId) return [];
  const positions: GridPosition[] = [];
  grid.forEach((column, reel) => {
    column.forEach((symbolId, row) => {
      if (symbolId === scatterSymbolId) positions.push({ reel, row });
    });
  });
  return positions;
}

function adaptFeature(
  feature: RgsFeatureWin,
  grid: readonly (readonly string[])[],
  context: RgsSessionContext,
  options: RgsAdaptOptions,
): FeatureTriggerDto {
  let previousAward = 0;
  const spins: FeatureSpinDto[] = feature.data.map((step, index) => {
    if (step.freeSpinsCount !== index + 1) {
      throw new GameError(
        'SPIN_RESPONSE_INVALID',
        `Feature '${feature.name}' step ${index} has count ${step.freeSpinsCount}; expected ${index + 1}.`,
      );
    }
    if (step.freeSpinsAmount < step.freeSpinsCount || step.freeSpinsAmount < previousAward) {
      throw new GameError(
        'SPIN_RESPONSE_INVALID',
        `Feature '${feature.name}' has an invalid awarded-spin total at step ${index}.`,
      );
    }
    previousAward = step.freeSpinsAmount;

    const wins = step.winDescription.map((entry, entryIndex) =>
      adaptPaylineWin(entry, `${feature.name}-${index}-${entryIndex}`, context),
    );
    const winsTotalMinor = wins.reduce((sum, win) => sum + win.amountMinor, 0);
    const serverStepTotalMinor = toMinorUnits(step.totalWin, context.currencyExponent);
    if (winsTotalMinor !== serverStepTotalMinor) {
      throw new GameError(
        'SPIN_RESPONSE_INVALID',
        `Feature '${feature.name}' step ${index} win parts total ${winsTotalMinor}, ` +
          `server total ${serverStepTotalMinor}.`,
      );
    }
    return {
      grid: step.screen,
      wins,
      totalWinMinor: serverStepTotalMinor,
    };
  });

  const finalAward = feature.data.at(-1)?.freeSpinsAmount ?? 0;
  if (feature.data.length > 0 && finalAward !== feature.data.length) {
    throw new GameError(
      'SPIN_RESPONSE_INVALID',
      `Feature '${feature.name}' resolved ${feature.data.length} steps but its final award is ${finalAward}.`,
    );
  }

  return {
    featureId: feature.name,
    type:
      feature.name === 'freeSpins' || feature.name === 'promoFreeSpins' ? 'freeSpins' : 'bonusGame',
    triggerPositions: triggerPositions(grid, options.scatterSymbolId),
    spins,
    totalWinMinor: toMinorUnits(feature.totalWin, context.currencyExponent),
  };
}

/** Validated RGS spin payload -> the DTO gameplay code consumes. */
export function adaptSpinResponse(
  wire: RgsSpinResponse,
  request: SpinRequestDto,
  context: RgsSessionContext,
  options: RgsAdaptOptions = {},
): SpinResultDto {
  const exponent = wire.currencyExponent ?? context.currencyExponent;
  const roundRef = `${context.sessionId}:${wire.sequenceNumber}`;

  const baseWins = wire.winDescription
    .filter((entry): entry is RgsPaylineWin => entry.type === 'paylineWin')
    .map((entry, index) => adaptPaylineWin(entry, `${roundRef}-${index}`, context));

  const features = wire.winDescription
    .filter((entry): entry is RgsFeatureWin => entry.type === 'featureWin')
    .map((entry) => adaptFeature(entry, wire.screen, context, options));

  const betAmountMinor = wire.betAmountMinor ?? toMinorUnits(wire.betAmount, exponent);
  const totalWinMinor = wire.winAmountMinor ?? toMinorUnits(wire.winAmount, exponent);
  const balanceAfterMinor = wire.balanceAfterMinor ?? toMinorUnits(wire.balance, exponent);
  const roundFunding =
    wire.campaignRoundsRemaining === undefined || wire.campaignRoundsRemaining === null
      ? 'cash'
      : 'campaign';

  // Guard the one place rounding could bite: converting each part separately must agree with
  // converting the server's own total. A mismatch means either a contract change or an amount
  // finer than the currency's minor unit, and both should fail loudly rather than silently
  // showing the player a number the server never sent.
  const partsTotal =
    baseWins.reduce((sum, win) => sum + win.amountMinor, 0) +
    features.reduce((sum, feature) => sum + feature.totalWinMinor, 0);
  if (partsTotal !== totalWinMinor) {
    throw new GameError(
      'SPIN_RESPONSE_INVALID',
      `RGS win amounts do not add up: parts total ${partsTotal}, round total ${totalWinMinor}.`,
    );
  }

  return {
    requestId: wire.requestId ?? request.requestId,
    sessionId: wire.sessionId ?? context.sessionId,
    roundId: wire.roundId ?? roundRef,
    transactionId: wire.transactionId ?? roundRef,
    gameId: request.gameId,
    currency: wire.currency ?? context.currency,
    betAmountMinor,
    // Pre-A4 the server does not report the opening balance, so it is reconstructed. That makes
    // the validator's arithmetic check tautological for those payloads — it only becomes a real
    // independent check once the server sends balanceBefore itself.
    balanceBeforeMinor:
      wire.balanceBeforeMinor ??
      (wire.balanceBefore !== undefined
        ? toMinorUnits(wire.balanceBefore, exponent)
        : roundFunding === 'campaign'
          ? balanceAfterMinor - totalWinMinor
          : balanceAfterMinor + betAmountMinor - totalWinMinor),
    balanceAfterMinor,
    totalWinMinor,
    roundFunding,
    ...(wire.campaignRoundsRemaining !== undefined
      ? { campaignRoundsRemaining: wire.campaignRoundsRemaining }
      : {}),
    ...(wire.promoFreeSpinsTriggered !== undefined
      ? { promoFreeSpinsTriggered: wire.promoFreeSpinsTriggered }
      : {}),
    grid: wire.screen,
    wins: baseWins,
    featuresTriggered: features,
    serverTime: wire.createdAt,
  };
}
